/**
 * bss-discount-fix.js
 *
 * Problem:
 *   Aplikacja BSS B2B Solution po stronie klienta wstrzykuje do
 *   `<p data-bss-needs-discount-fix>` własne ceny netto / brutto:
 *     <span class="bss-b2b-price-no-vat">279,00 zł excl. VAT</span>
 *     <span class="bss-b2b-price-has-vat">343,17 zł incl. VAT</span>
 *   Nie wie jednak o rabatach koszykowych Shopify (kody rabatowe),
 *   więc pokazuje kwoty PRZED rabatem.
 *
 * Rozwiązanie:
 *   1. Liquid wystawia rabat przez `data-bss-discount-cents` albo dokładną
 *      cenę Shopify przez `data-bss-final-unit-cents`. Dokładna cena jest
 *      potrzebna m.in. wtedy, gdy dwa wiersze mają ten sam wariant, ale tylko
 *      jeden z nich otrzymał rabat promocyjny.
 *   2. Reagujemy na zdarzenia świadczące o tym, że właściwa sekcja koszyka
 *      została wyrenderowana:
 *        - `DOMContentLoaded` (strona koszyka),
 *        - `eurus:section:rendered` z `id === 'cart-drawer'` (AJAX-owy drawer),
 *        - `eurus:cart:items-changed` (re-render po zmianie ilości).
 *   3. Lokalne obserwatory reagują na wstrzyknięcia BSS, a obserwator strony
 *      odnajduje nowe targety, gdy aplikacja wymieni cały wiersz lub stopkę.
 *      Korygujemy LICZBĘ wewnątrz każdego spanu, zachowując etykiety.
 */

(() => {
  'use strict';

  const PRICE_SELECTOR = '.bss-b2b-price-no-vat, .bss-b2b-price-has-vat';
  const APPLIED_ATTR = 'data-bss-discount-applied';
  const RELEVANT_SECTIONS = new Set(['cart-drawer']);

  /**
   * Parsuje tekst typu „279,00 zł excl. VAT” → { token, value, decimals,
   * thousandSep, decimalSep }. Zwraca `null`, jeśli liczby nie ma.
   */
  const parsePrice = (text) => {
    const match = String(text).match(/-?\d[\d .,\u00A0]*\d|-?\d/);
    if (!match) return null;

    const token = match[0];
    const isNegative = token.startsWith('-');
    const raw = token.replace(/^-/, '').replace(/\u00A0/g, ' ');

    const lastDot = raw.lastIndexOf('.');
    const lastComma = raw.lastIndexOf(',');
    const hasSpace = /\s/.test(raw);

    const fmt = (() => {
      if (lastDot !== -1 && lastComma !== -1) {
        const decimalSep = lastDot > lastComma ? '.' : ',';
        return { decimalSep, thousandSep: decimalSep === '.' ? ',' : '.' };
      }
      if (lastDot !== -1) {
        const after = raw.length - lastDot - 1;
        return after === 1 || after === 2
          ? { decimalSep: '.', thousandSep: hasSpace ? ' ' : null }
          : { decimalSep: null, thousandSep: '.' };
      }
      if (lastComma !== -1) {
        const after = raw.length - lastComma - 1;
        return after === 1 || after === 2
          ? { decimalSep: ',', thousandSep: hasSpace ? ' ' : null }
          : { decimalSep: null, thousandSep: ',' };
      }
      return { decimalSep: null, thousandSep: hasSpace ? ' ' : null };
    })();

    const decimals = fmt.decimalSep
      ? raw.length - (fmt.decimalSep === '.' ? lastDot : lastComma) - 1
      : 0;

    const normalized = fmt.decimalSep
      ? raw.split(fmt.thousandSep || '\0').join('').replace(fmt.decimalSep, '.')
      : raw.replace(/[.,\s\u00A0]/g, '');

    const value = Number.parseFloat(normalized);
    if (!Number.isFinite(value)) return null;

    return {
      token,
      value: isNegative ? -value : value,
      decimals,
      thousandSep: fmt.thousandSep,
      decimalSep: fmt.decimalSep,
    };
  };

  /**
   * Formatuje `amount` z powrotem do stringa w tym samym stylu, w którym
   * BSS wstrzyknął cenę (przecinek/kropka, spacje tysięcy, liczba miejsc).
   */
  const formatPrice = (info, amount) => {
    const abs = Math.abs(amount).toFixed(info.decimals);
    const [intPart, decPart] = abs.split('.');
    const grouped = info.thousandSep
      ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, info.thousandSep)
      : intPart;
    const sign = amount < 0 ? '-' : '';
    return decPart
      ? `${sign}${grouped}${info.decimalSep || '.'}${decPart}`
      : `${sign}${grouped}`;
  };

  const NO_VAT_CLASS = 'bss-b2b-price-no-vat';
  const HAS_VAT_CLASS = 'bss-b2b-price-has-vat';

  /**
   * Koryguje jeden span: zmienia tylko liczbę, etykieta tekstowa zostaje.
   * Idempotentne – nie wpadamy w pętlę observera.
   */
  const correctPriceNode = (node, discount) => {
    const current = node.textContent;
    if (!current) return;
    if (node.getAttribute(APPLIED_ATTR) === current) return;

    const info = parsePrice(current);
    if (!info) {
      node.setAttribute(APPLIED_ATTR, current);
      return;
    }

    const corrected = Math.max(0, info.value - discount);
    const newText = current.replace(info.token, formatPrice(info, corrected));
    if (newText === current) {
      node.setAttribute(APPLIED_ATTR, current);
      return;
    }
    node.textContent = newText;
    node.setAttribute(APPLIED_ATTR, newText);
  };

  const setPriceNode = (node, amount) => {
    const current = node.textContent;
    if (!current) return false;
    if (node.getAttribute(APPLIED_ATTR) === current) return true;

    const info = parsePrice(current);
    if (!info) return false;

    const newText = current.replace(info.token, formatPrice(info, amount));
    if (newText !== current) node.textContent = newText;
    node.setAttribute(APPLIED_ATTR, newText);
    return true;
  };

  /**
   * Odczytuje rabat i tryb podatkowy koszyka z danych wystawionych przez
   * Liquid. `cart.total_discount` ma ten sam tryb podatkowy co ceny Shopify:
   * jest kwotą brutto, gdy podatki są wliczone, albo netto, gdy nie są.
   */
  const getDiscountContext = (target) => {
    const root = target.closest(
      '[data-bss-discount-cents], [data-bss-force-final-price]',
    );
    if (!root) {
      return {
        discount: 0,
        taxesIncluded: false,
        forcedFinalPrice: null,
        forcedFinalPriceText: '',
      };
    }

    const cents = Number(root.getAttribute('data-bss-discount-cents'));
    const finalPriceCents = Number(root.getAttribute('data-bss-final-unit-cents'));
    const forceFinalPrice =
      root.getAttribute('data-bss-force-final-price') === 'true';
    return {
      discount: Number.isFinite(cents) && cents > 0 ? cents / 100 : 0,
      taxesIncluded:
        root.getAttribute('data-bss-taxes-included') === 'true',
      forcedFinalPrice:
        forceFinalPrice && Number.isFinite(finalPriceCents)
          ? finalPriceCents / 100
          : null,
      forcedFinalPriceText:
        root.getAttribute('data-bss-final-price-text') || '',
    };
  };

  /**
   * Wylicza stosunek brutto/netto z aktualnych (jeszcze nieskorygowanych)
   * wartości BSS w obrębie targetu. Gdy brak jednego ze spanów lub liczb
   * nie da się sparsować, zwraca 1 (brak korekty proporcjonalnej).
   */
  const getVatRatio = (target) => {
    const noVat = target.querySelector(`.${NO_VAT_CLASS}`);
    const hasVat = target.querySelector(`.${HAS_VAT_CLASS}`);
    if (!noVat || !hasVat) return 1;

    const noVatInfo = parsePrice(
      noVat.getAttribute(APPLIED_ATTR) || noVat.textContent,
    );
    const hasVatInfo = parsePrice(
      hasVat.getAttribute(APPLIED_ATTR) || hasVat.textContent,
    );
    if (!noVatInfo || !hasVatInfo || noVatInfo.value <= 0) return 1;
    return hasVatInfo.value / noVatInfo.value;
  };

  const fixTarget = (target) => {
    const {
      discount,
      taxesIncluded,
      forcedFinalPrice,
      forcedFinalPriceText,
    } = getDiscountContext(target);

    if (forcedFinalPrice !== null) {
      const ratio = getVatRatio(target);
      const nodes = Array.from(target.querySelectorAll(PRICE_SELECTOR));
      const allNodesCorrected =
        nodes.length > 0 &&
        nodes.every((node) => {
          const isGrossPrice = node.classList.contains(HAS_VAT_CLASS);
          const amount = taxesIncluded
            ? isGrossPrice
              ? forcedFinalPrice
              : forcedFinalPrice / ratio
            : isGrossPrice
              ? forcedFinalPrice * ratio
              : forcedFinalPrice;
          return setPriceNode(node, amount);
        });

      if (!allNodesCorrected && forcedFinalPriceText) {
        if (target.textContent.trim() !== forcedFinalPriceText) {
          target.textContent = forcedFinalPriceText;
        }
      }
      return;
    }

    if (discount <= 0) return;

    const ratio = getVatRatio(target);

    target.querySelectorAll(PRICE_SELECTOR).forEach((node) => {
      const isGrossPrice = node.classList.contains(HAS_VAT_CLASS);
      const nodeDiscount = taxesIncluded
        ? isGrossPrice
          ? discount
          : discount / ratio
        : isGrossPrice
          ? discount * ratio
          : discount;
      correctPriceNode(node, nodeDiscount);
    });
  };

  const watched = new WeakSet();

  const watchTarget = (target) => {
    if (watched.has(target)) {
      fixTarget(target);
      return;
    }
    watched.add(target);

    const mo = new MutationObserver(() => fixTarget(target));
    mo.observe(target, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // BSS mógł już zdążyć wstrzyknąć ceny – sprawdzamy od razu.
    fixTarget(target);
  };

  const scan = () => {
    document
      .querySelectorAll('[data-bss-needs-discount-fix]')
      .forEach(watchTarget);
  };

  const scheduleScans = () => {
    [0, 50, 150, 400, 1000, 2000].forEach((delay) => {
      window.setTimeout(scan, delay);
    });
  };

  /**
   * BSS sometimes replaces a complete cart row or footer after the theme has
   * already emitted its cart event. Observers attached to the old price nodes
   * cannot see that replacement, so discover every newly inserted target too.
   */
  const observeReplacedTargets = () => {
    const root = document.body || document.documentElement;
    if (!root) return;

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          if (node.matches('[data-bss-needs-discount-fix]')) {
            watchTarget(node);
          }
          node
            .querySelectorAll('[data-bss-needs-discount-fix]')
            .forEach(watchTarget);
        });
      });
    });

    mo.observe(root, { childList: true, subtree: true });
  };

  const init = () => {
    scan();
    observeReplacedTargets();
    scheduleScans();

    document.addEventListener('eurus:section:rendered', (e) => {
      if (RELEVANT_SECTIONS.has(e.detail?.id)) {
        scheduleScans();
      }
    });

    document.addEventListener('eurus:cart:items-changed', () => {
      scheduleScans();
    });
    document.addEventListener('eurus:cart:discount-code:change', () => {
      scheduleScans();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
